import { useCallback, useEffect } from 'react';
import { fetchDataLegacy, fetchDataWithAsyncReportResolve } from 'reporting-data/v2/reportingApi/snowflake/fetchData';
import { useAutoCancel } from 'reporting-data/v2/reportingApi/snowflake/useAutoCancel';
// @ts-expect-error untyped dependency
import { useDifferent, useReportToQuery } from './queryUtils';
import usePromiseEffect from '../../shared/hooks/usePromiseEffect';
import { DeprecatedDataSourcesError, MaximumColumnsCountExceededError, ReportWithOptions } from 'reporting-snowflake/relational/resolve/resolve-records';
// @ts-expect-error migrate upstream file
import { reportHasDeprecatedDataSources } from 'reporting-snowflake/relational/utils/report-deprecation-utils';
import { doesColumnCountExceedExpandedMaximum } from 'reporting-snowflake/relational/utils/report-utils';
import { useCancelQuery } from 'reporting-data/v2/reportingApi/snowflake/useCancelQuery';
import { useReportingLogger } from '../../PlatformReport/ReportingLoggerProvider';

/**
 * @param {ReportWithOptions} reportToQuery
 * @param {function} onSuccess
 * @param {number} reportId
 * @returns {{queryState, refetch }}
 */

/*
 * This logic is migrated from reporting-snowflake (https://git.hubteam.com/HubSpot/reporting/blob/e95210c8a9e4bda64fb8ca0937dce158e6b8fc8d/reporting-snowflake/static-1.49562/js/relational/resolve/request.ts#L228)
 */
const checkBeforeResolve = reportWithOptions => {
  if (doesColumnCountExceedExpandedMaximum(reportWithOptions.reportDefinition)) {
    return new MaximumColumnsCountExceededError('Report has too many columns');
  }
  if (reportHasDeprecatedDataSources(reportWithOptions.reportDefinition)) {
    return new DeprecatedDataSourcesError('Report has deprecated data sources');
  }
  return undefined;
};

// This is a copy of useQueryRaaS, but with async report resolve enabled
// the difference is that we are using fetchDataWithAsyncReportResolve instead of fetchData
// and we are not using autoCancel in favor of the useCancelQuery which use abortController to cancel the query
export const useQueryRaasWithAsyncReportResolve = (reportWithOptions, {
  reportId,
  onSuccess,
  queryId,
  isValidation
}) => {
  const {
    logger
  } = useReportingLogger('platformReportResolveLogger');
  logger === null || logger === void 0 || logger.addAttribute('isAsyncResolve', true);
  /**
   * reportToQuery: ReportWithOptions memoized on data query requirements
   */
  const reportToQuery = useReportToQuery(reportWithOptions, reportId);
  const abortControllerRef = useCancelQuery(reportToQuery);
  useEffect(() => {}, [abortControllerRef]);
  const refetch = useCallback(() => {
    var _abortControllerRef$c;
    const validationError = checkBeforeResolve(reportToQuery);
    if (validationError) {
      return Promise.reject(validationError);
    }
    return fetchDataWithAsyncReportResolve({
      payload: reportToQuery,
      options: {
        signal: abortControllerRef === null || abortControllerRef === void 0 || (_abortControllerRef$c = abortControllerRef.current) === null || _abortControllerRef$c === void 0 ? void 0 : _abortControllerRef$c.signal
      }
    });
  }, [reportToQuery, abortControllerRef]);

  /**
   * refetch either dataset requirements change
   */
  const differentRequirements = useDifferent(reportToQuery);
  const shouldRefetch = ({
    data
  }) => {
    const shouldRefetchResult = !!reportToQuery && (!isValidation || queryId !== undefined) && (data === undefined || !!differentRequirements);
    return shouldRefetchResult;
  };
  const queryState = usePromiseEffect(refetch, {
    enabledFn: shouldRefetch,
    onSuccess
  });
  return {
    queryState,
    refetch,
    queryDefinition: reportToQuery
  };
};
const useQueryRaaS = (reportWithOptions, {
  reportId,
  onSuccess,
  queryId,
  isValidation
}) => {
  /**
   * reportToQuery: ReportWithOptions memoized on data query requirements
   */
  const reportToQuery = useReportToQuery(reportWithOptions, reportId);
  const autoCancel = useAutoCancel(reportToQuery);
  const refetch = useCallback(() => {
    const validationError = checkBeforeResolve(reportToQuery);
    if (validationError) {
      return Promise.reject(validationError);
    }
    return fetchDataLegacy(reportToQuery, {
      queryId,
      onTick: autoCancel
    });
  }, [reportToQuery, autoCancel, queryId]);

  /**
   * refetch either dataset requirements change
   */
  const differentRequirements = useDifferent(reportToQuery);
  const shouldRefetch = ({
    data
  }) => !!reportToQuery && (!isValidation || queryId !== undefined) && (data === undefined || !!differentRequirements);
  const queryState = usePromiseEffect(refetch, {
    enabledFn: shouldRefetch,
    onSuccess
  });
  return {
    queryState,
    refetch,
    queryDefinition: reportToQuery
  };
};
export default useQueryRaaS;